import type { EventSourceMessage } from "@microsoft/fetch-event-source";
import type {
  ChatMessage,
  ChatMessageContent,
  ContextItem,
} from "schemas/generated/chatMessage";

import React, { useCallback, useEffect, useRef, useState } from "react";

import { Chat } from "@editor/components/editor/ai/chat/Chat";
import AssetComponent from "@editor/components/editor/ai/chat/MessageInput/assetNodeExtension";
import ProductComponent from "@editor/components/editor/ai/chat/MessageInput/productNodeExtension";
import { parseChatTokens } from "@editor/components/editor/ai/chat/parseChatTokens";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { getTokenFromStorage } from "@editor/infra/auth";
import { trpc, trpcClient, trpcUtils } from "@editor/utils/trpc";

import { getPublisherUrl } from "@/config";
import { getAssetUrlsFromFiles } from "@/features/assets/useUploadAsset";
import { useWebContainer } from "@/features/web-container/context";
import {
  detect200StatusCode,
  detectBuildError,
  detectFileStatusError,
} from "@/features/web-container/useWebContainerHook";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { errorToast } from "@replo/design-system/components/alert/Toast";
import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import { skipToken, useMutation } from "@tanstack/react-query";
import Placeholder from "@tiptap/extension-placeholder";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useParams, useSearchParams } from "react-router-dom";
import { v4 } from "uuid";

import { useManageChats } from "../../hooks/chat";
import { useReactRenderStore } from "../../store/reactRenderStore";
import { useProjectContentUpdate } from "../ProjectWebsocketProvider";
import { useChatStoreActions, useIsChatLoading } from "./store/chatStore";
import {
  chatEditorHandleKeyDown,
  chatEditorOnUpdate,
  getCleanedChatPrompt,
} from "./utils/tiptapUtils";

interface StreamingState {
  isStreaming: boolean;
  controller?: AbortController;
  currentChatId?: string;
}

export default function ReactEditorChat() {
  const projectId = useCurrentProjectId();
  const { user } = useCurrentUser();
  const { pageId } = useParams();
  const { handleNewChat } = useManageChats();
  const { mountAdditionalFiles } = useWebContainer();

  const [searchParams] = useSearchParams();
  const chatId = searchParams.get("chatId");

  const { setContextMenuOpen, contextMenuOpen } = useReactRenderStore();

  const isChatLoading = useIsChatLoading();
  const {
    setIsChatLoading,
    setMessages,
    clearMessages,
    addMessages,
    addOrUpdateMessage,
    setViewingVersionId,
  } = useChatStoreActions();

  const [files, setFiles] = useState<File[] | null>(null);
  const streamingStateRef = useRef<StreamingState>({ isStreaming: false });
  const isSubmittingRef = useRef(false);

  const [atPosition, setAtPosition] = React.useState<number | null>(null);

  const { logs } = useWebContainer();
  const [isMonitoringLogs, setIsMonitoringLogs] = useState(false);
  const [logStartIndex, setLogStartIndex] = useState(0);
  const hasCalledHandleFileErrorRef = useRef(false);

  // Refetch project fileTree whenever a CONTENT_UPDATE websocket event is received
  useProjectContentUpdate(
    () =>
      void trpcUtils.webContainer.getFileTree.invalidate({
        projectId,
      }),
  );

  const { mutateAsync: uploadFiles } = useMutation({
    mutationFn: ({ files, projectId }: { files: File[]; projectId: string }) =>
      getAssetUrlsFromFiles(files, projectId),
    onError: (error) => {
      console.error("Failed to upload files:", error);
      setIsChatLoading(false);
    },
  });

  const { data: chat, isLoading: isLoadingChat } = trpc.chat.get.useQuery(
    projectId && chatId ? { chatId, projectId } : skipToken,
    // NOTE Bobby (2025-06-12): This is needed beacause our default trpc/react-query staleTime is infinity. This means when we initially fetch
    // with this query key, the messages will be empty/outdated so when you navigate to a different chat and come back,
    // it will be out of date. Easier to set this as 0 and have it refetch on backend every time since it's not expensive and less logic than invalidating everywhere we care to.
    // TODO Bobby (2025-06-12): We should change the default staleTime to 0
    {
      staleTime: 0,
    },
  );

  const { mutateAsync: revertErroredVersion } =
    trpc.nextJSProject.revertErroredVersion.useMutation({
      onSuccess: async () => {
        // NOTE (Bobby: 2025-07-11): We set a timeout of 2 seconds here because of some webcontainer race condition where if mountAdditionalFiles is called
        // multiple times in a row, the rendered file will only be the first one. Some hot reload issue. https://linear.app/replo/issue/REPL-17915/investigate-hot-reloading-race-condition-in-web-containers
        // In this case, it's fine to wait 2 seconds since the revert is really fast and it's reasonable to wait 2 seconds for the file to be reverted.
        await new Promise((resolve) => setTimeout(resolve, 2000));
        void updatePageFiles();
      },
    });

  const { mutate: addMessage } = trpc.chat.addMessage.useMutation({
    onSuccess: () => {
      if (chatId) {
        void trpcUtils.chat.get.invalidate({
          chatId,
          projectId,
        });
      }
    },
  });
  const { mutate: updateMessage } = trpc.chat.updateMessage.useMutation({
    onSuccess: () => {
      if (chatId) {
        void trpcUtils.chat.get.invalidate({
          chatId,
          projectId,
        });
      }
    },
  });

  // Only sync from server when not in the middle of a submission
  useEffect(() => {
    if (chat?.messages && !isSubmittingRef.current) {
      setMessages(chat.messages);
    } else if (chatId === null) {
      clearMessages();
    }
  }, [chat?.messages, setMessages, chatId, clearMessages]);

  const updatePageFiles = useCallback(async () => {
    if (!projectId || !pageId) {
      return;
    }

    const fileTree = await trpcClient.webContainer.getFileTreeForPage.query({
      projectId,
      pageId,
    });
    await mountAdditionalFiles?.(fileTree);
  }, [projectId, pageId, mountAdditionalFiles]);

  const handleStreamMessage = useCallback(
    (ev: EventSourceMessage) => {
      if (ev.event === "message") {
        const messageData = JSON.parse(ev.data);
        if (messageData.type === "assistant" || messageData.type === "tool") {
          const isToolResult =
            messageData.content.type === "array" &&
            messageData.content.content[0].type === "tool-result";
          if (isToolResult) {
            void updatePageFiles();
            const toolResult = messageData.content.content[0].result;
            if (toolResult.fileVersionId) {
              setViewingVersionId(toolResult.fileVersionId);
            }
          }
          const currentChatId = streamingStateRef.current.currentChatId;
          if (currentChatId) {
            addOrUpdateMessage(
              messageData.id,
              messageData.content,
              messageData.type,
              currentChatId,
            );
          }
        }
      } else if (ev.event === "done") {
        streamingStateRef.current.isStreaming = false;
        setIsChatLoading(false);
      } else if (ev.event === "error") {
        console.error("Stream error:", ev.data);
        streamingStateRef.current.isStreaming = false;
        setIsChatLoading(false);
        setIsMonitoringLogs(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      addOrUpdateMessage,
      setIsChatLoading,
      updatePageFiles,
      setViewingVersionId,
    ],
  );

  const prepareAssetUrls = useCallback(
    async (files: File[] | null) => {
      let assetUrls: string[] = [];
      if (files && files.length > 0) {
        assetUrls = await uploadFiles({ files, projectId: projectId! });
      }

      return [...assetUrls];
    },
    [uploadFiles, projectId],
  );

  // NOTE (Bobby: 2025-06-24): This function is used to create all the new user messages that will be added to the chat history. It is the user prompt as
  // well as the context items they've selected. This is added to the chat history on the frontend as well as sent to the backend to be persisted.
  const createNewUserMessages = useCallback(
    async (chatId: string, currentPrompt: string): Promise<ChatMessage[]> => {
      if (!projectId) {
        return [];
      }

      const contextMessages: ChatMessageContent["content"] = [];
      const assetUrls = await prepareAssetUrls(files);

      // This adds the asset urls that are inline in the prompt.
      // TODO (Bobby: 2025-06-27): Separate this out into a helper function
      const segments = parseChatTokens(currentPrompt);
      const promptAssetUrls = segments
        .filter(
          (segment) =>
            segment.type === "chat-token" &&
            segment.elementData?.type === "asset",
        )
        .map((segment) => segment.elementData?.data.url);

      assetUrls.forEach((url) => {
        contextMessages.push({
          type: "text",
          text: `The image url is ${url}`,
          isHidden: true,
        });
        contextMessages.push({
          type: "image",
          image: url,
        });
      });

      promptAssetUrls.forEach((url) => {
        contextMessages.push({
          type: "image",
          image: url,
          isHidden: true,
        });
      });

      return [
        ...(contextMessages.length > 0
          ? [
              {
                id: v4(),
                content: {
                  type: "array" as const,
                  content: contextMessages,
                },
                createdAt: new Date(),
                role: "user" as const,
                chatId,
              },
            ]
          : []),
        {
          id: v4(),
          content: {
            type: "string" as const,
            content: currentPrompt,
          },
          createdAt: new Date(),
          role: "user" as const,
          chatId,
        },
      ];
    },
    [files, prepareAssetUrls, projectId],
  );

  const setupStreaming = useCallback(() => {
    // Abort any existing stream
    if (streamingStateRef.current.controller) {
      streamingStateRef.current.controller.abort();
    }

    const controller = new AbortController();
    streamingStateRef.current.isStreaming = true;
    streamingStateRef.current.controller = controller;
    return controller;
  }, []);

  const createContextItems = useCallback(async (): Promise<ContextItem[]> => {
    const assetUrls = await prepareAssetUrls(files);

    const contextItems: ContextItem[] = [
      ...assetUrls.map(
        (url): ContextItem => ({
          type: "assetUrl",
          url,
        }),
      ),
      {
        type: "openPage" as const,
        pageId: pageId ?? "",
      },
    ];

    return contextItems;
  }, [files, prepareAssetUrls, pageId]);

  const startChatStream = useCallback(
    async (
      newUserMessages: ChatMessage[],
      chatId: string,
      controller: AbortController,
    ) => {
      const PUBLISHER_URL = getPublisherUrl();
      const token = getTokenFromStorage();

      const contextItems = await createContextItems();

      await fetchEventSource(`${PUBLISHER_URL}/api/v2/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Token ${token}`,
        },
        body: JSON.stringify({
          newUserMessages,
          projectId,
          chatId,
          contextItems,
        }),
        signal: controller.signal,
        openWhenHidden: true,
        onmessage: handleStreamMessage,
        onclose() {
          streamingStateRef.current.isStreaming = false;
          setIsChatLoading(false);
        },
        onerror(err) {
          console.error("Streaming error:", err);
          streamingStateRef.current.isStreaming = false;
          setIsChatLoading(false);
        },
      });
    },
    [projectId, handleStreamMessage, setIsChatLoading, createContextItems],
  );

  const handleFileError = useCallback(async () => {
    if (!pageId || !projectId) {
      return;
    }
    setIsMonitoringLogs(false);
    setIsChatLoading(true);

    // NOTE (Bobby: 2025-07-07): This case shouldn't really happen but is here just in case to prevent it from infinitely looping and
    // starting a new chat to undo last changes. Case where this happens is when it tries to undo an error but that doesn't work and it's still in an error state
    if (hasCalledHandleFileErrorRef.current === true) {
      errorToast(
        "Error",
        "There was an error with that last change. Please reach out to support at support@replo.app",
      );
      return;
    }

    const buildErrorMessageId = v4();

    addMessage({
      id: buildErrorMessageId,
      role: "assistant",
      projectId,
      content: {
        type: "array",
        content: [
          {
            type: "text",
            contentType: "buildError",
            text: JSON.stringify({
              type: "buildError",
              isReverting: true,
            }),
          },
        ],
      },
      chatId: chatId!,
    });

    const previousVersion = await revertErroredVersion({
      projectId,
      fileId: pageId,
    });

    if (previousVersion) {
      setViewingVersionId(previousVersion.id);
      updateMessage({
        id: buildErrorMessageId,
        projectId,
        content: {
          type: "array",
          content: [
            {
              type: "text",
              contentType: "buildError",
              text: JSON.stringify({
                type: "buildError",
                isReverting: false,
              }),
            },
          ],
        },
      });
    }

    setIsChatLoading(false);
  }, [
    chatId,
    addMessage,
    projectId,
    updateMessage,
    setViewingVersionId,
    pageId,
    revertErroredVersion,
    setIsChatLoading,
  ]);

  // NOTE (Bobby: 2025-07-01): This effect is used to monitor new logs from a chat stream. Doing this so we only call handleBuildError from an error caused by a change from chat
  // and NOT some other error. e.g. we don't want to call handleBuildError if the project is misconfigured or something.
  useEffect(() => {
    if (!isMonitoringLogs) {
      return;
    }

    const newLogs = logs.slice(logStartIndex);
    const hasFileError = newLogs.some((log) =>
      detectFileStatusError(log.message),
    );
    // NOTE (Bobby: 2025-07-07): Need this separate build error check because when there's build error it doesn't log out the status code
    const hasBuildError = newLogs.some((log) => detectBuildError(log.message));
    const has200StatusCode = newLogs.some((log) =>
      detect200StatusCode(log.message),
    );

    if (
      (hasFileError || hasBuildError) &&
      !streamingStateRef.current.isStreaming
    ) {
      void handleFileError();
      return;
    }

    if (has200StatusCode) {
      setIsMonitoringLogs(false);
      return;
    }
  }, [logs, isMonitoringLogs, logStartIndex, handleFileError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // NOTE (Bobby: 2025-06-30): This converts the current prompt which is a collection of tip tap nodes into a cleaner string to (1) make
    // it more readable for the llm and (2) standardize the messages so it's easier to parse it when rendering message history
    if (!chatEditor) {
      return;
    }
    const currentPrompt = getCleanedChatPrompt(chatEditor);

    if (isChatLoading || !projectId || !user?.id || !pageId || !currentPrompt) {
      return;
    }

    isSubmittingRef.current = true;

    let currentChatId = chatId;

    if (!chatId) {
      const newChat = await handleNewChat();
      if (!newChat) {
        return;
      }
      currentChatId = newChat.id;
    }

    streamingStateRef.current.currentChatId = currentChatId!;

    chatEditor.commands.setContent("", false);
    setFiles(null);
    setIsChatLoading(true);

    setLogStartIndex(logs.length);
    setIsMonitoringLogs(true);
    hasCalledHandleFileErrorRef.current = false;

    try {
      const newUserMessages = await createNewUserMessages(
        currentChatId!,
        currentPrompt,
      );
      addMessages(newUserMessages);

      const controller = setupStreaming();
      await startChatStream(newUserMessages, currentChatId!, controller);
    } catch (error) {
      setIsMonitoringLogs(false);
      if (error instanceof Error && error.name !== "AbortError") {
        console.error("Chat submission error:", error);
      }
      streamingStateRef.current.isStreaming = false;
      setIsChatLoading(false);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const chatEditor = useEditor({
    extensions: [
      StarterKit,
      AssetComponent,
      ProductComponent,
      Placeholder.configure({
        placeholder: "Ask for any edits or customizations",
      }),
    ],
    content: "",
    onUpdate: chatEditorOnUpdate(
      contextMenuOpen,
      setAtPosition,
      setContextMenuOpen,
      atPosition,
    ),
    editorProps: {
      attributes: {
        style: "font-size: 14px;",
        class: "react-editor-chat",
      },
      handleKeyDown: chatEditorHandleKeyDown(
        contextMenuOpen,
        setAtPosition,
        setContextMenuOpen,
        atPosition,
        handleSubmit,
      ),
    },
  });

  const handleStop = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (streamingStateRef.current.controller) {
        streamingStateRef.current.controller.abort();
      }
      streamingStateRef.current.isStreaming = false;
      setIsChatLoading(false);
    },
    [setIsChatLoading],
  );

  useEffect(() => {
    const currentController = streamingStateRef.current.controller;

    return () => {
      if (currentController) {
        currentController.abort();
      }
    };
  }, []);

  if (isLoadingChat) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-muted text-sm">
          <Spinner size={20} variant="secondary" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col gap-4">
      <style>
        {`
          .react-editor-chat.tiptap p.is-editor-empty:first-child::before {
            color: var(--color-slate-500);
            content: attr(data-placeholder);
            float: left;
            height: 0;
            pointer-events: none;
          }
        `}
      </style>
      <Chat
        ownerId={chat?.userId}
        handleSubmit={(e) => void handleSubmit(e)}
        stop={handleStop}
        files={files}
        setFiles={setFiles}
        editor={chatEditor}
      />
    </div>
  );
}
